import { DownloadIcon } from '@chakra-ui/icons';
import { Box, Center, HStack, Spinner, VStack } from '@chakra-ui/react';
import { BlobProvider } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { useEffect, useMemo, useRef, useState } from 'react';

import Button from 'components/Button';
import Modal from 'components/Modal';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';

import { CVData, CVSettings, CVTemplate } from '../types';
import CVPDFDocument from '../utils/pdfRenderer';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  data: CVData;
  settings: CVSettings;
  template: CVTemplate;
}

const ExportDialog = ({ isOpen, onClose, data, settings, template }: ExportDialogProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { isMobile } = useMediaQueries();
  const blobRef = useRef<Blob | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Reset when dialog opens so we get a fresh PDF generation
  useEffect(() => {
    if (isOpen) {
      blobRef.current = null;
      setIsReady(false);
    }
  }, [isOpen]);

  const fileName = [
    data.personalInfo.fullName?.replace(/\s+/g, '_') || 'CV',
    data.personalInfo.jobTitle?.replace(/\s+/g, '_') || '',
    'CV',
  ]
    .filter(Boolean)
    .join('_')
    .replace(/_+/g, '_');

  const handleDownload = () => {
    if (!blobRef.current) return;

    setIsDownloading(true);
    saveAs(blobRef.current, `${fileName}.pdf`);
    setIsDownloading(false);
  };

  const footer = (
    <HStack spacing={3} width="100%" justifyContent="flex-end">
      <Button scheme="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button
        scheme="primary"
        onClick={handleDownload}
        isLoading={isDownloading}
        isDisabled={!isReady}
        leftIcon={<DownloadIcon />}
      >
        Download PDF
      </Button>
    </HStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isMobile ? 'full' : '6xl'}
      header="Export CV as PDF"
      footer={footer}
    >
      {isOpen && (
        <ExportContent
          data={data}
          settings={settings}
          template={template}
          isMobile={isMobile}
          onBlobReady={(blob) => {
            blobRef.current = blob;
            setIsReady(true);
          }}
        />
      )}
    </Modal>
  );
};

/** Inner content that mounts/unmounts with dialog — ensures fresh PDF each time */
const ExportContent = ({
  data, settings, template, isMobile, onBlobReady,
}: {
  data: CVData;
  settings: CVSettings;
  template: CVTemplate;
  isMobile: boolean;
  onBlobReady: (blob: Blob) => void;
}) => {
  const onBlobReadyRef = useRef(onBlobReady);
  onBlobReadyRef.current = onBlobReady;
  const notifiedRef = useRef(false);

  // Memoize the document so BlobProvider doesn't re-generate on parent re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pdfDocument = useMemo(() => <CVPDFDocument data={data} settings={settings} template={template} />, []);

  return (
    <BlobProvider document={pdfDocument}>
      {({ blob, loading, error }) => {
        if (blob && !notifiedRef.current) {
          notifiedRef.current = true;
          setTimeout(() => onBlobReadyRef.current(blob), 0);
        }

        if (loading) {
          return (
            <Center py={20}>
              <VStack spacing={3}>
                <Spinner size="lg" color="purple.400" />
                <Text fontSize="small" opacity="secondary">
                  Generating PDF preview...
                </Text>
              </VStack>
            </Center>
          );
        }

        if (error) {
          return (
            <Center py={20}>
              <Text fontSize="small" color="red.400">
                Error generating PDF. Please try again.
              </Text>
            </Center>
          );
        }

        return (
          <Box
            width="100%"
            height={isMobile ? 'calc(100vh - 240px)' : '65vh'}
            borderRadius="md"
            overflow="hidden"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            {blob && <PdfIframe blob={blob} />}
          </Box>
        );
      }}
    </BlobProvider>
  );
};

/** Stable iframe that only creates the object URL once per blob instance */
const PdfIframe = ({ blob }: { blob: Blob }) => {
  const url = useMemo(() => URL.createObjectURL(blob), [blob]);

  useEffect(() => {
    return () => URL.revokeObjectURL(url);
  }, [url]);

  return (
    <iframe
      src={url}
      width="100%"
      height="100%"
      title="CV PDF Preview"
      style={{ border: 'none' }}
    />
  );
};

export default ExportDialog;
